<template>
  <div
    :style="iconCutStyles.around"
    :class="wrapperClass"
    :aria-disabled="disabled || undefined"
  >
    <i
      style="
        font-variation-settings:
          'FILL' 0,
          'wght' 250,
          'GRAD' 0,
          'opsz' 0;
      "
      :style="iconInnerStyle"
      class="material-symbols-outlined leading-none remove-built-in-padding"
      :class="iconElementClass"
      :title="tooltip"
      @click="onClick"
    >
      {{ materialIcon }}
    </i>
  </div>
</template>

<script>
import {ICONS_MAPPING, DEFAULT_ICON} from './HitIcon.js';

const MATERIAL = ICONS_MAPPING.material;
const DEFAULT_MATERIAL_ICON = MATERIAL[DEFAULT_ICON];

const SIZE_TO_REM = {
  '2xs': 1,
  xs: 1.125,
  sm: 1.25,
  base: 1.5,
  md: 1.875,
  lg: 2.25,
  xl: 3.75,
};

const SIZE_TO_TAILWIND = {
  '2xs': 'text-base',
  xs: 'text-lg',
  sm: 'text-xl',
  base: 'text-2xl',
  md: 'text-3xl',
  lg: 'text-4xl',
  xl: 'text-6xl',
};

const COLOR_TO_TAILWIND = {
  light: 'text-light',
  'light-2': 'text-light-2',
  warning: 'text-warning-icon',
  danger: 'text-danger-icon',
  white: 'text-white',
  default: 'text-default',
  success: 'text-success-icon',
  clickable: 'text-clickable',
  header: 'text-header',
  card: 'text-card',
  input: 'text-input',
  button: 'text-button',
  'button-floating': 'text-button-floating',
};

export default {
  name: 'HitIcon',
  components: {},
  inheritAttrs: false, //needed to make onClick not fall through to <i> which would cause two calls to onClick
  props: {
    /**
     * Display pointer cursor on hover
     */
    clickable: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable click
     */
    disabled: Boolean,
    /**
     * Icon tooltip
     */
    tooltip: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Icon name to display
     */
    icon: {
      type: String,
      required: true,
      default: DEFAULT_ICON,
    },
    /**
     * Icon size. Available values : 2xs, xs, sm, base, md, lg, xl.
     */
    size: {
      type: String,
      default: 'base',
      validator: function (value) {
        // The value must match one of these strings
        return (
          ['2xs', 'xs', 'sm', 'base', 'md', 'lg', 'xl'].indexOf(value) !== -1
        );
      },
    },
    /**
     * Icon color.
     */
    color: {
      type: String,
      default: 'card',
    },
    iconColorStyle: {
      type: Object,
      required: false,
    },
    /**
     * Rotates the icon by the given degree value.
     */
    rotation: {
      type: Number,
      default: 0,
    },
    /**
     * removes some of the padding inherent to the icon by making the containing div smaller and cutting overflow
     * specified in rem
     */
    cutWidth: {
      type: Number,
      default: 0,
    },
    /**
     * removes some of the padding inherent to the icon by making the containing div smaller and cutting overflow
     * specified in rem
     */
    cutHeight: {
      type: Number,
      default: 0,
    },
    additionalClasses: {
      type: String,
      default: '',
    },
    inTable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    materialIcon() {
      const mappingData = MATERIAL[this.icon] ?? DEFAULT_MATERIAL_ICON;
      if (typeof mappingData === 'string') return mappingData;
      return mappingData.icon;
    },
    totalRotation() {
      const mappingData = MATERIAL[this.icon] ?? DEFAULT_MATERIAL_ICON;
      const iconRotation = mappingData?.rotation ?? 0;
      return iconRotation + this.rotation;
    },
    iconFound() {
      return this.materialIcon !== DEFAULT_MATERIAL_ICON;
    },

    /** Classes to apply for the wrapper div */
    wrapperClass() {
      const classes = [this.additionalClasses];
      if (this.cutWidth <= 0 && this.cutHeight <= 0)
        classes.push('flex', 'items-center');
      if (this.disabled) {
        classes.push('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
      }
      return classes;
    },

    /** Classes to apply for the icon <i> element */
    iconElementClass() {
      const classes = [SIZE_TO_TAILWIND[this.size] ?? 'text-2xl'];
      if (!this.iconColorStyle)
        classes.push(COLOR_TO_TAILWIND[this.color] ?? this.color);
      if (!this.iconFound) classes.push('text-danger', 'text-6xl');
      if (!this.disabled && (this.clickable || this.tooltip)) {
        classes.push('cursor-pointer');
      }
      if (this.inTable) classes.push('remove-padding-manually');
      return classes;
    },

    iconCutStyles() {
      if (this.cutWidth <= 0 && this.cutHeight <= 0) {
        return {around: {}, transform: {}};
      }
      const rem = SIZE_TO_REM[this.size] ?? 1.5;
      return {
        around: {
          width: rem - this.cutWidth + 'rem',
          height: rem - this.cutHeight + 'rem',
          overflow: 'hidden',
        },
        transform: {
          transform: `translate(-${this.cutWidth / 2}rem,-${
            this.cutHeight / 2
          }rem)`,
        },
      };
    },
    iconInnerStyle() {
      const baseTransform = this.iconCutStyles.transform?.transform || '';
      const rotationTransform =
        this.totalRotation !== 0 ? ` rotate(${this.totalRotation}deg)` : '';
      const mergedTransform = `${baseTransform}${rotationTransform}`.trim();
      const computedTransform = mergedTransform
        ? {transform: mergedTransform}
        : {};
      const customStyle = this.iconColorStyle || {};

      return {
        ...computedTransform,
        ...customStyle,
      };
    },
  },
  methods: {
    onClick: function ($event) {
      if (!this.disabled && this.clickable) {
        /**
         * When the icon is clicked
         */
        this.$emit('click', $event);
      }
    },
  },
};
</script>
